'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DensityCalculator() {
  const t = useTranslations("tools.densityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [currentMode, setCurrentMode] = useState<'density' | 'mass' | 'volume'>('density');
  const [inputs, setInputs] = useState({
    density: '',
    densityUnit: 'kg/m3',
    mass: '8900',
    massUnit: 'kg',
    volume: '1',
    volumeUnit: 'm3'
  });
  const [result, setResult] = useState<{ value: string; unit: string; label: string } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Conversion factors
  const massConversions: { [key: string]: number } = {
    'kg': 1,
    'g': 0.001,
    'mg': 0.000001,
    't': 1000,
    'lb': 0.45359237,
    'oz': 0.028349523125,
    'ct': 0.0002
  };

  const volumeConversions: { [key: string]: number } = {
    'm3': 1,
    'L': 0.001,
    'mL': 0.000001,
    'gal-us': 0.003785411784,
    'gal-uk': 0.00454609,
    'ft3': 0.028316846592,
    'yd3': 0.764554857984,
    'in3': 0.000016387064,
    'cm3': 0.000001
  };

  const densityConversions: { [key: string]: number } = {
    'kg/m3': 1,
    'kg/cm3': 1000000,
    'g/m3': 0.001,
    'g/cm3': 1000,
    'kg/L': 1000,
    'g/L': 1,
    'lb/in3': 27679.904710191,
    'lb/ft3': 16.018463373960,
    'lb/yd3': 0.59327642110871,
    'lb/gal-us': 119.82642731689,
    'lb/gal-uk': 99.776372663101
  };

  const convertToBase = (value: number, unit: string, conversions: { [key: string]: number }) => {
    return value * conversions[unit];
  };

  const convertFromBase = (value: number, unit: string, conversions: { [key: string]: number }) => {
    return value / conversions[unit];
  };

  const handleModeChange = (mode: 'density' | 'mass' | 'volume') => {
    setCurrentMode(mode);
    setResult(null);

    if (mode === 'density') {
      setInputs({
        ...inputs,
        density: '',
        mass: '8900',
        volume: '1'
      });
    } else if (mode === 'mass') {
      setInputs({
        ...inputs,
        mass: '',
        density: '',
        volume: '1'
      });
    } else if (mode === 'volume') {
      setInputs({
        ...inputs,
        volume: '',
        density: '',
        mass: '8900'
      });
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (currentMode === 'density') {
        const mass = parseFloat(inputs.mass);
        const volume = parseFloat(inputs.volume);

        if (!mass || !volume || mass <= 0 || volume <= 0) {
          alert(t('validationError'));
          return;
        }

        const massKg = convertToBase(mass, inputs.massUnit, massConversions);
        const volumeM3 = convertToBase(volume, inputs.volumeUnit, volumeConversions);
        const densityKgM3 = massKg / volumeM3;

        const resultValue = convertFromBase(densityKgM3, inputs.densityUnit, densityConversions);
        const unitLabel = getDensityUnitLabel(inputs.densityUnit);

        setResult({
          value: resultValue.toFixed(6),
          unit: unitLabel,
          label: t('densityLabel')
        });

      } else if (currentMode === 'mass') {
        const density = parseFloat(inputs.density);
        const volume = parseFloat(inputs.volume);

        if (!density || !volume || density <= 0 || volume <= 0) {
          alert(t('validationError'));
          return;
        }

        const densityKgM3 = convertToBase(density, inputs.densityUnit, densityConversions);
        const volumeM3 = convertToBase(volume, inputs.volumeUnit, volumeConversions);
        const massKg = densityKgM3 * volumeM3;

        const resultValue = convertFromBase(massKg, inputs.massUnit, massConversions);
        const unitLabel = getMassUnitLabel(inputs.massUnit);

        setResult({
          value: resultValue.toFixed(6),
          unit: unitLabel,
          label: t('massLabel')
        });

      } else if (currentMode === 'volume') {
        const density = parseFloat(inputs.density);
        const mass = parseFloat(inputs.mass);

        if (!density || !mass || density <= 0 || mass <= 0) {
          alert(t('validationError'));
          return;
        }

        const densityKgM3 = convertToBase(density, inputs.densityUnit, densityConversions);
        const massKg = convertToBase(mass, inputs.massUnit, massConversions);
        const volumeM3 = massKg / densityKgM3;

        const resultValue = convertFromBase(volumeM3, inputs.volumeUnit, volumeConversions);
        const unitLabel = getVolumeUnitLabel(inputs.volumeUnit);

        setResult({
          value: resultValue.toFixed(6),
          unit: unitLabel,
          label: t('volumeLabel')
        });
      }
    } catch (error) {
      console.error(error);
      alert(t('calculationError'));
    }
  };

  const handleClear = () => {
    handleModeChange(currentMode);
  };

  const getDensityUnitLabel = (unit: string) => {
    const labels: { [key: string]: string } = {
      'kg/m3': 'kg/m³',
      'kg/cm3': 'kg/cm³',
      'g/m3': 'g/m³',
      'g/cm3': 'g/cm³',
      'kg/L': 'kg/L',
      'g/L': 'g/L',
      'lb/in3': 'lb/in³',
      'lb/ft3': 'lb/ft³',
      'lb/yd3': 'lb/yd³',
      'lb/gal-us': 'lb/gal (US)',
      'lb/gal-uk': 'lb/gal (UK)'
    };
    return labels[unit] || unit;
  };

  const getMassUnitLabel = (unit: string) => {
    const labels: { [key: string]: string } = {
      'kg': 'kg',
      'g': 'g',
      'mg': 'mg',
      't': 't',
      'lb': 'lb',
      'oz': 'oz',
      'ct': 'ct'
    };
    return labels[unit] || unit;
  };

  const getVolumeUnitLabel = (unit: string) => {
    const labels: { [key: string]: string } = {
      'm3': 'm³',
      'L': 'L',
      'mL': 'mL',
      'gal-us': 'gal (US)',
      'gal-uk': 'gal (UK)',
      'ft3': 'ft³',
      'yd3': 'yd³',
      'in3': 'in³',
      'cm3': 'cm³'
    };
    return labels[unit] || unit;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    {},
    {},
    {}
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '1rem',
      marginTop: '1rem',
      borderRadius: '0.5rem',
      fontWeight: 500
    },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' }
  );

  const modeSelectorStyle = getResponsiveStyle(
    {
      display: 'flex',
      gap: '0.5rem',
      background: '#f8fafc',
      borderBottom: '2px solid #e2e8f0',
      flexWrap: 'wrap' as const,
      justifyContent: 'center' as const
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const modeBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      border: '2px solid #cbd5e1',
      background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
      color: isActive ? 'white' : '#475569',
      borderColor: isActive ? 'transparent' : '#cbd5e1',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease'
    },
    { padding: '0.6rem 1rem', fontSize: '0.9rem' },
    { padding: '0.7rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGridStyle = {
    display: 'grid',
    gap: '1.5rem'
  };

  const formGroupStyle = {
    display: 'grid',
    gap: '0.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputRowStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '0.75rem',
      alignItems: 'center'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr auto' },
    { gridTemplateColumns: '1fr auto' }
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      width: '100%'
    },
    {},
    {},
    {}
  );

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem'
    },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      padding: '2rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac'
    },
    { margin: '1.5rem' },
    { margin: '1.75rem' },
    { margin: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#166534' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem'
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#1e293b'
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const infoSectionStyle = getResponsiveStyle(
    {
      padding: '1.5rem',
      background: '#fef3c7',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #f59e0b'
    },
    { margin: '1.5rem' },
    { margin: '1.75rem' },
    { margin: '2rem' }
  );

  const infoH4Style = {
    margin: '0 0 0.5rem 0',
    color: '#92400e',
    fontSize: '1rem'
  };

  const infoPStyle = {
    margin: 0,
    color: '#78350f',
    fontSize: '0.9rem',
    lineHeight: 1.5
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = {
    paddingLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⚖️ {t('toolTitle')}</h2>
            <div style={formulaDisplayStyle}>{t('formula')}</div>
          </div>

          <div style={modeSelectorStyle}>
            <button
              type="button"
              style={modeBtnStyle(currentMode === 'density')}
              onClick={() => handleModeChange('density')}
            >
              {t('findDensity')}
            </button>
            <button
              type="button"
              style={modeBtnStyle(currentMode === 'mass')}
              onClick={() => handleModeChange('mass')}
            >
              {t('findMass')}
            </button>
            <button
              type="button"
              style={modeBtnStyle(currentMode === 'volume')}
              onClick={() => handleModeChange('volume')}
            >
              {t('findVolume')}
            </button>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGridStyle}>
              {/* Density Input */}
              <div style={formGroupStyle}>
                <label htmlFor="density" style={labelStyle}>{t('densityLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    id="density"
                    step="any"
                    style={inputStyle}
                    placeholder={currentMode === 'density' ? '= ?' : t('enterDensity')}
                    value={inputs.density}
                    onChange={(e) => setInputs({ ...inputs, density: e.target.value })}
                    disabled={currentMode === 'density'}
                  />
                  <select
                    id="density-unit"
                    style={selectStyle}
                    value={inputs.densityUnit}
                    onChange={(e) => setInputs({ ...inputs, densityUnit: e.target.value })}
                  >
                    <option value="kg/m3">kg/m³</option>
                    <option value="kg/cm3">kg/cm³</option>
                    <option value="g/m3">g/m³</option>
                    <option value="g/cm3">g/cm³</option>
                    <option value="kg/L">kg/L</option>
                    <option value="g/L">g/L</option>
                    <option value="lb/in3">lb/in³</option>
                    <option value="lb/ft3">lb/ft³</option>
                    <option value="lb/yd3">lb/yd³</option>
                    <option value="lb/gal-us">lb/gal (US)</option>
                    <option value="lb/gal-uk">lb/gal (UK)</option>
                  </select>
                </div>
              </div>

              {/* Mass Input */}
              <div style={formGroupStyle}>
                <label htmlFor="mass" style={labelStyle}>{t('massLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    id="mass"
                    step="any"
                    style={inputStyle}
                    placeholder={currentMode === 'mass' ? '= ?' : t('enterMass')}
                    value={inputs.mass}
                    onChange={(e) => setInputs({ ...inputs, mass: e.target.value })}
                    disabled={currentMode === 'mass'}
                  />
                  <select
                    id="mass-unit"
                    style={selectStyle}
                    value={inputs.massUnit}
                    onChange={(e) => setInputs({ ...inputs, massUnit: e.target.value })}
                  >
                    <option value="kg">kilogram (kg)</option>
                    <option value="g">gram (g)</option>
                    <option value="mg">milligram (mg)</option>
                    <option value="t">metric ton (t)</option>
                    <option value="lb">pound (lb)</option>
                    <option value="oz">ounce (oz)</option>
                    <option value="ct">carat (ct)</option>
                  </select>
                </div>
              </div>

              {/* Volume Input */}
              <div style={formGroupStyle}>
                <label htmlFor="volume" style={labelStyle}>{t('volumeLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    id="volume"
                    step="any"
                    style={inputStyle}
                    placeholder={currentMode === 'volume' ? '= ?' : t('enterVolume')}
                    value={inputs.volume}
                    onChange={(e) => setInputs({ ...inputs, volume: e.target.value })}
                    disabled={currentMode === 'volume'}
                  />
                  <select
                    id="volume-unit"
                    style={selectStyle}
                    value={inputs.volumeUnit}
                    onChange={(e) => setInputs({ ...inputs, volumeUnit: e.target.value })}
                  >
                    <option value="m3">cubic meter (m³)</option>
                    <option value="L">liter (L)</option>
                    <option value="mL">milliliter (mL)</option>
                    <option value="gal-us">gallon (US)</option>
                    <option value="gal-uk">gallon (UK)</option>
                    <option value="ft3">cubic foot (ft³)</option>
                    <option value="yd3">cubic yard (yd³)</option>
                    <option value="in3">cubic inch (in³)</option>
                    <option value="cm3">cubic centimeter (cm³)</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>
          </form>

          {result && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>✨ {t('resultsTitle')}</h3>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{result.label}</span>
                <span style={resultValueStyle}>{result.value} {result.unit}</span>
              </div>
            </div>
          )}

          <div style={infoSectionStyle}>
            <h4 style={infoH4Style}>💡 {t('tipTitle')}</h4>
            <p style={infoPStyle}>{t('tipContent')}</p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.calculatorsoup.com/calculators/physics/density.php" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Density Calculator p = m/V - Calculator Soup
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.gigacalculator.com/calculators/density-calculator.php" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Density Calculator - GIGACalculator.com
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.sensorsone.com/mass-and-density-to-volume-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Mass & Density to Volume Calculator - SensorsONE
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.inchcalculator.com/density-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Density Calculator - Inch Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/density-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Density Calculator - Calculator.net
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}